/**
 * Payment gateway — `/finance/gateway`.
 *
 * Three collections that belong together: the charges, the exception queue,
 * and the settlement transfers.
 *
 * A charge that cannot be attributed to a customer is neither lost nor guessed
 * at: it lands on the exception queue with the reason stated, and one payment
 * has at most one open exception. Resolving one is a decision with an optional
 * note, so the queue defaults to the OPEN exceptions — the only ones anybody
 * has to act on.
 *
 * Reconciling a payout asserts the gateway clearing account nets to zero for
 * that settlement before anything posts (`bank.clearing_does_not_net`).
 * Naming the bank statement line the settlement arrived on makes that line's
 * own posting the settlement's journal instead of emitting a second one, so
 * the field is offered rather than hidden.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@erp/ui";

import { DataTable, badgeCell, dateTimeCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  GATEWAY_PAYMENT_STATUSES,
  GATEWAY_PAYOUT_STATUSES,
  reconcileGatewayPayout,
  replayGatewayEvent,
  resolveGatewayPaymentException,
  type GatewayEvent,
  type GatewayPayment,
  type GatewayPaymentException,
  type GatewayPayout,
} from "../api";
import { PageHeader, ProblemBanner, SelectField, TextField } from "../components/fields";
import {
  financeKeys,
  useGatewayExceptionList,
  useGatewayPaymentList,
  useGatewayPayoutList,
  type GatewayPaymentFilters,
  type GatewayPayoutFilters,
} from "../queries";

const ALL = "__all__";

const PAYMENT_STATUS_VARIANTS = {
  pending: "outline",
  matched: "secondary",
  exception: "destructive",
  settled: "success",
} as const;

const PAYOUT_STATUS_VARIANTS = {
  pending: "warning",
  reconciled: "success",
} as const;

export interface GatewayPageProps {
  canResolve?: boolean;
  canReplay?: boolean;
  canReconcilePayout?: boolean;
  pageLimit?: number;
}

export function GatewayPage({
  canResolve = true,
  canReplay = true,
  canReconcilePayout = true,
  pageLimit,
}: GatewayPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [paymentStatus, setPaymentStatus] = useState<string>(ALL);
  const [payoutStatus, setPayoutStatus] = useState<string>(ALL);
  const [openOnly, setOpenOnly] = useState<string>("true");
  const [details, setDetails] = useState<Record<string, string>>({});
  const [statementLineIds, setStatementLineIds] = useState<Record<string, string>>({});
  const [eventId, setEventId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const paymentFilters = useMemo<GatewayPaymentFilters>(
    () =>
      paymentStatus === ALL
        ? {}
        : { status: paymentStatus as NonNullable<GatewayPaymentFilters["status"]> },
    [paymentStatus],
  );
  const payoutFilters = useMemo<GatewayPayoutFilters>(
    () =>
      payoutStatus === ALL
        ? {}
        : { status: payoutStatus as NonNullable<GatewayPayoutFilters["status"]> },
    [payoutStatus],
  );

  const paymentsQuery = useGatewayPaymentList(paymentFilters, pageLimit);
  const exceptionsQuery = useGatewayExceptionList({ open: openOnly === "true" }, pageLimit);
  const payoutsQuery = useGatewayPayoutList(payoutFilters, pageLimit);

  const resolveMutation = useIdempotentMutation<
    GatewayPaymentException,
    { gatewayExceptionId: string; detail: string }
  >({
    mutationFn: (input, key) =>
      resolveGatewayPaymentException(
        input.gatewayExceptionId,
        input.detail === "" ? {} : { detail: input.detail },
        key,
      ),
    onSuccess: (_result, variables) => {
      setDetails((current) => ({ ...current, [variables.gatewayExceptionId]: "" }));
      void queryClient.invalidateQueries({ queryKey: financeKeys.gateway.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const replayMutation = useIdempotentMutation<GatewayEvent, { gatewayEventId: string }>({
    mutationFn: (input, key) => replayGatewayEvent(input.gatewayEventId, key),
    onSuccess: () => {
      setEventId("");
      void queryClient.invalidateQueries({ queryKey: financeKeys.gateway.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const reconcileMutation = useIdempotentMutation<
    GatewayPayout,
    { gatewayPayoutId: string; statementLineId: string }
  >({
    mutationFn: (input, key) =>
      reconcileGatewayPayout(
        input.gatewayPayoutId,
        input.statementLineId === "" ? {} : { bank_statement_line_id: input.statementLineId },
        key,
      ),
    onSuccess: (_result, variables) => {
      setStatementLineIds((current) => ({ ...current, [variables.gatewayPayoutId]: "" }));
      void queryClient.invalidateQueries({ queryKey: financeKeys.gateway.all });
      void queryClient.invalidateQueries({ queryKey: financeKeys.bankStatementLines.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const paymentColumns = useMemo<ColumnDef<GatewayPayment>[]>(
    () => [
      {
        accessorKey: "provider",
        id: "provider",
        header: t("finance.gateway.columns.provider", "Provider"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "charge_id",
        id: "charge_id",
        header: t("finance.gateway.columns.chargeId", "Charge"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "captured_at",
        id: "captured_at",
        header: t("finance.gateway.columns.capturedAt", "Captured"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        id: "gross_amount",
        header: t("finance.gateway.columns.gross", "Gross"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.gross_amount} />,
      },
      {
        id: "fee_amount",
        header: t("finance.gateway.columns.fee", "Fee"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.fee_amount} />,
      },
      {
        id: "net_amount",
        header: t("finance.gateway.columns.net", "Net"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.net_amount} />,
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("finance.gateway.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            PAYMENT_STATUS_VARIANTS,
            t(`finance.gateway.paymentStatus.${value}`, value),
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.gateway.title", "Payment gateway")}
        description={t(
          "finance.gateway.subtitle",
          "Charges, the exceptions nobody could attribute, and the settlements that carry them to the bank.",
        )}
      />

      <Tabs defaultValue="exceptions">
        <TabsList>
          <TabsTrigger value="exceptions">
            {t("finance.gateway.tabExceptions", "Exceptions")}
          </TabsTrigger>
          <TabsTrigger value="payments">{t("finance.gateway.tabPayments", "Charges")}</TabsTrigger>
          <TabsTrigger value="payouts">{t("finance.gateway.tabPayouts", "Payouts")}</TabsTrigger>
        </TabsList>

        <TabsContent value="exceptions" className="flex flex-col gap-4">
          <Card>
            <CardContent className="flex flex-wrap items-end gap-3 pt-6">
              <SelectField
                id="gateway-exception-open"
                label={t("finance.gateway.filterOpen", "Show")}
                value={openOnly}
                onValueChange={setOpenOnly}
                className="w-52"
                options={[
                  { value: "true", label: t("finance.gateway.openOnly", "Unresolved only") },
                  { value: "false", label: t("finance.common.all", "All") },
                ]}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4 pt-6">
              {exceptionsQuery.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("finance.gateway.noExceptions", "Nothing is waiting on the exception queue.")}
                </p>
              ) : (
                exceptionsQuery.rows.map((exception) => (
                  <div key={exception.id} className="flex flex-col gap-2 rounded-md border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">
                        {t(`finance.gateway.exceptionReason.${exception.reason}`, exception.reason)}
                      </span>
                      <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                        {exception.gateway_payment_id}
                      </span>
                    </div>
                    {exception.detail === undefined || exception.detail === "" ? null : (
                      <p className="text-sm">{exception.detail}</p>
                    )}
                    {exception.resolved_at !== null ? (
                      <p className="text-sm text-muted-foreground">
                        {t("finance.gateway.resolvedAt", "Resolved")}{" "}
                        <span dir="ltr">{exception.resolved_at}</span>
                      </p>
                    ) : canResolve ? (
                      <div className="flex flex-wrap items-end gap-2">
                        <TextField
                          id={`exception-detail-${exception.id}`}
                          label={t("finance.gateway.resolutionDetail", "What was done")}
                          value={details[exception.id] ?? ""}
                          onChange={(value) =>
                            setDetails((current) => ({ ...current, [exception.id]: value }))
                          }
                          className="min-w-64 flex-1"
                          maxLength={400}
                        />
                        <Button
                          type="button"
                          size="sm"
                          loading={resolveMutation.isPending}
                          onClick={() =>
                            resolveMutation.mutate({
                              gatewayExceptionId: exception.id,
                              detail: (details[exception.id] ?? "").trim(),
                            })
                          }
                        >
                          {t("finance.gateway.resolve", "Close the exception")}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {canReplay ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.gateway.replaySection", "Replay a gateway event")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-end gap-3">
                <TextField
                  id="gateway-event-id"
                  label={t("finance.gateway.eventId", "Gateway event")}
                  value={eventId}
                  onChange={setEventId}
                  className="min-w-72 flex-1"
                  ltr
                />
                <Button
                  type="button"
                  loading={replayMutation.isPending}
                  disabled={eventId.trim() === ""}
                  onClick={() => {
                    setFormError(null);
                    replayMutation.mutate({ gatewayEventId: eventId.trim() });
                  }}
                >
                  {t("finance.gateway.replay", "Replay")}
                </Button>
                <p className="w-full text-sm text-muted-foreground">
                  {t(
                    "finance.gateway.replayHint",
                    "Re-processes a stored notification. A redelivery of the same event is a no-op.",
                  )}
                </p>
                {formError === null ? null : (
                  <div className="w-full">
                    <ProblemBanner>{formError}</ProblemBanner>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="payments" className="flex flex-col gap-4">
          <Card>
            <CardContent className="flex flex-wrap items-end gap-3 pt-6">
              <SelectField
                id="gateway-payment-status"
                label={t("finance.gateway.filterStatus", "Status")}
                value={paymentStatus}
                onValueChange={setPaymentStatus}
                className="w-52"
                options={[
                  { value: ALL, label: t("finance.common.all", "All") },
                  ...GATEWAY_PAYMENT_STATUSES.map((value) => ({
                    value,
                    label: t(`finance.gateway.paymentStatus.${value}`, value),
                  })),
                ]}
              />
            </CardContent>
          </Card>
          <DataTable
            columns={paymentColumns}
            query={paymentsQuery}
            getRowId={(row) => row.id}
            storageKey="finance.gateway.payments.columns"
            emptyMessage={t("finance.gateway.noPayments", "No charges match these filters.")}
          />
        </TabsContent>

        <TabsContent value="payouts" className="flex flex-col gap-4">
          <Card>
            <CardContent className="flex flex-wrap items-end gap-3 pt-6">
              <SelectField
                id="gateway-payout-status"
                label={t("finance.gateway.filterStatus", "Status")}
                value={payoutStatus}
                onValueChange={setPayoutStatus}
                className="w-52"
                options={[
                  { value: ALL, label: t("finance.common.all", "All") },
                  ...GATEWAY_PAYOUT_STATUSES.map((value) => ({
                    value,
                    label: t(`finance.gateway.payoutStatus.${value}`, value),
                  })),
                ]}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col gap-4 pt-6">
              {payoutsQuery.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("finance.gateway.noPayouts", "No settlements match these filters.")}
                </p>
              ) : (
                payoutsQuery.rows.map((payout) => (
                  <div key={payout.id} className="flex flex-col gap-2 rounded-md border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span dir="ltr" className="font-mono text-sm font-medium">
                        {payout.payout_id}
                      </span>
                      {badgeCell(
                        payout.status,
                        PAYOUT_STATUS_VARIANTS,
                        t(`finance.gateway.payoutStatus.${payout.status}`, payout.status),
                      )}
                      <span className="text-sm text-muted-foreground">
                        <span dir="ltr">{payout.paid_on}</span>
                      </span>
                      <MoneyDisplay value={payout.net_amount} />
                    </div>
                    {payout.status === "reconciled" || !canReconcilePayout ? null : (
                      <div className="flex flex-wrap items-end gap-2">
                        <TextField
                          id={`payout-line-${payout.id}`}
                          label={t("finance.gateway.statementLine", "Bank statement line")}
                          value={statementLineIds[payout.id] ?? ""}
                          onChange={(value) =>
                            setStatementLineIds((current) => ({ ...current, [payout.id]: value }))
                          }
                          hint={t(
                            "finance.gateway.statementLineHint",
                            "Optional. Naming it makes that line's posting the settlement's journal instead of a second one.",
                          )}
                          className="min-w-64 flex-1"
                          ltr
                        />
                        <Button
                          type="button"
                          size="sm"
                          loading={reconcileMutation.isPending}
                          onClick={() =>
                            reconcileMutation.mutate({
                              gatewayPayoutId: payout.id,
                              statementLineId: (statementLineIds[payout.id] ?? "").trim(),
                            })
                          }
                        >
                          {t("finance.gateway.reconcile", "Reconcile the settlement")}
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
